import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Pages from '../../../Data/Viewer.json';
import Loader from '../../../Components/Loader/Loader';
import Button from '../../../Components/Button/Button';
import InputField from '../../../Components/InputField/InputField';
import styles from './viewer.module.scss';
import { dailyUpdate, getItems } from '../../../Services/api.service';
import { unixTimestampToStringDate } from '../../../Components/DatePicker/DatePicker';
import { Helmet } from 'react-helmet-async';
import { formatCurrency } from '../../../Components/LeadCard/LeadCard';
import checkMark from '../../../Assets/Images/Icons/Other/check.png';
import xMark from '../../../Assets/Images/Icons/Other/xmark.png';

const THIRTY_DAYS_MS = 2592000000;

const Viewer = () => {
    const userRole = localStorage.getItem('role');
    const navigate = useNavigate();
    const loadMoreRef = useRef(null); // Ref to the "load more" element
    const { title } = useParams();
    const saved = JSON.parse(localStorage.getItem(`${title}-filter`)) || [];
    const [selectedButton, setSelectedButton] = useState(saved[0] || '');
    const [copyList, setCopyList] = useState([]);
    const [totalNumberOfItems, setTotalNumberOfItems] = useState(0);
    const [page, setPage] = useState({});
    const [kwargs, setKwargs] = useState("");
    const [loading, setLoading] = useState(true);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState(saved[1] || '');
    const [btnFilterActivated, setBtnFilterActivated] = useState(false);
    const [keywordFilterActivated, setKeywordFilterActivated] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    let dataRender;
    let buttonRender;

    useEffect(() => {
        (async () => {
            setLoading(true);
            const foundPage = Pages.find((p) => p.title === title);
            foundPage[title] = await getItems(foundPage.title, true);
            setPage(foundPage || {});
            setCopyList(foundPage[title].results);
            setTotalNumberOfItems(foundPage[title].count);

            if (title === "invoices") {
                const total = foundPage[title].total;
                setTotal(total);
            }
            setLoading(false);
        })();
    // eslint-disable-next-line
    }, [title, refreshKey]);

    useEffect(() => {
        setKwargs("");
        const storedValue = localStorage.getItem(`${title}-searchKeyword`) || '';
        if (storedValue) {
            setKwargs(storedValue);
        }
    }, [title]);

    useEffect(() => {
        localStorage.setItem(`${title}-searchKeyword`, kwargs);
        if (kwargs !== "") {
            setKeywordFilterActivated(true);
        }
    }, [kwargs, title]);

    useEffect(() => {
        if (!keywordFilterActivated) return;
        setFetchLoading(true);

        if (kwargs !== '') {
            const delaySearch = setTimeout(async () => {
                try {
                    // Make the search request when kwargs is not empty
                    const searchPage = await getItems(page.title, false, 1, kwargs);
                    setTotalNumberOfItems(searchPage.count);
                    setCopyList(searchPage.results);
                    if (title === "invoices") {
                        const total = searchPage.total;
                        setTotal(total);
                    }
                } catch (error) {
                    console.error('Error fetching search results:', error);
                } finally {
                    setFetchLoading(false);
                }
            }, 800); // Waits 800ms before triggering search

            return () => clearTimeout(delaySearch); // Cleanup to cancel previous call if user types again
        }

        if (kwargs === "") {
            const delaySearch = setTimeout(async () => {
                try {
                    const resetPage = await getItems(page.title, false, 1, filter, true);
                    setTotalNumberOfItems(resetPage.count);
                    setCopyList(resetPage.results);
                    if (title === "invoices") {
                        const total = resetPage.total;
                        setTotal(total);
                    }
                } catch (error) {
                    console.error('Error fetching search results:', error);
                } finally {
                    setKeywordFilterActivated(false);
                    setFetchLoading(false);
                }
            }, 800); // Waits 800ms before triggering search

            return () => clearTimeout(delaySearch); // Cleanup to cancel previous call if user types again
        }
    // eslint-disable-next-line
    }, [kwargs, page, keywordFilterActivated]);

    useEffect(() => {
        // Store the current ref value in a variable
        const currentLoadMoreRef = loadMoreRef.current;

        // Create the IntersectionObserver instance
        const observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(entry => {
                    // If the "load more" element is in view
                    if (entry.isIntersecting && !fetchLoading) {
                        if (copyList.length < totalNumberOfItems) {
                            fetchNextPage();
                        }
                    }
                });
            },
            {
                rootMargin: '0px',
                threshold: 1.0, // Trigger when 100% of the target is in view
            }
        );

        if (currentLoadMoreRef) {
            observer.observe(currentLoadMoreRef); // Start observing the load-more element
        }

        return () => {
            // Cleanup observer when the component unmounts or ref changes
            if (currentLoadMoreRef) {
                observer.unobserve(currentLoadMoreRef);
            }
        };
    // eslint-disable-next-line
    }, [copyList]);

    const fetchNextPage = async () => {
        setFetchLoading(true);
        try {
            const nextPage = await getItems(page.title, false, undefined, undefined, false);
            setCopyList(nextPage.results);
        } catch (error) {
            console.error("Error fetching next page:", error); // Handle any errors during fetch
        } finally {
            setFetchLoading(false); // Set loading state to false after the data is fetched
        }
    };

    useEffect(() => {
        const storedValue = JSON.parse(localStorage.getItem(`${title}-filter`)) || [];
        if (storedValue[1]) {
            setSelectedButton(storedValue[0]);
            setFilter(storedValue[1]);
            setBtnFilterActivated(true);
        }
    // eslint-disable-next-line
    }, [page]);

    useEffect(() => {
        if (!btnFilterActivated) return;

        const fetchData = async () => {
            setFetchLoading(true);
            try {
                let response;
                if (!filter) {
                    response = await getItems(title, false, 1, undefined, true);
                    setBtnFilterActivated(false);
                } else {
                    response = await getItems(title, true, undefined, filter);
                }
    
                setCopyList(response.results);
                setTotalNumberOfItems(response.count);
                if (title === "invoices") {
                    const total = response.total;
                    setTotal(total);
                }                
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setFetchLoading(false); // Always runs, even if error happens
            }
        };
    
        fetchData();
        // eslint-disable-next-line
    }, [filter]);

    const actionFunction = async (label, actionType, filterKeyword) => {
        const actionTypeFilter = `${filterKeyword}=${actionType}`;
    
        if (actionType === "new") {
            navigate(`/admin/${page.title}/${actionType}`);
            return;
        }
    
        if (actionType === "dailyUpdate") {
            setLoading(true);
            await dailyUpdate();
            setRefreshKey(prev => prev + 1);
            setLoading(false);
            return;
        }
    
        const isSameButton = label === selectedButton;
        const newLabel = isSameButton ? '' : label;
        const newFilter = isSameButton ? '' : actionTypeFilter;
    
        setSelectedButton(newLabel);
        setFilter(newFilter);
        setBtnFilterActivated(true);
    
        localStorage.setItem(`${title}-filter`, JSON.stringify([newLabel, newFilter]));
    };

    if (!loading) {
        dataRender = (copyList || [])
            .map((data) => (
                <tr key={data.id}>
                    <td>
                        <div className={styles.viewButtonWrapper}>
                            <Button
                                buttonText="View"
                                onClick={() => navigate(`/admin/${page['title']}/${data.id}`)}
                            />
                        </div>
                    </td>
                    {Object.keys(data)
                        .filter((key) => !page['ignoreKeys'].includes(key))
                        .map((key) => {
                            if (key.includes('date') || key.includes('Date')) {
                                if (title === "invoices") {
                                    return (
                                        <td
                                            key={key}
                                            className={`${
                                                data[key] &&
                                                !data["paid"] &&
                                                Date.now() - data[key] * 1000 > THIRTY_DAYS_MS
                                                    ? styles.alertDate
                                                    : ''
                                            }`}
                                        >
                                            <p>{data[key] ? unixTimestampToStringDate(data[key]) : '-'}</p>
                                        </td>
                                    );
                                } else {
                                    return (
                                        <td key={key}>
                                            {data[key] ? unixTimestampToStringDate(data[key]) : '-'}
                                        </td>
                                    );
                                }
                            }
                            else if (key.includes('companyName')) {
                                return (
                                    <td className={styles.smallFont} key={key}>
                                        {(data['companyName'] || data['clientName'])}
                                    </td>
                                );
                            }
                            else if (key.includes('billed') || key.includes('paid')) {
                                return (
                                    <td key={key} className={styles.columnWithIcons}>
                                        {data["billed"] ? 
                                            <img src={checkMark} alt = "checkMark" />
                                        :
                                            <img src={xMark} alt = "xMark" />
                                        }

                                        {data[key] ?
                                            <img src={checkMark} alt = "checkMark" />
                                        :
                                            <img src={xMark} alt = "xMark" />
                                        }
                                    </td>
                                );
                            }
                            else if (key.includes('invoiceNumber')) {
                                return (
                                    <td key={key}>
                                        { data[key] ?
                                            `# ${data[key]} - ${data["type"]}`
                                        :
                                            'No'
                                        }
                                    </td>
                                );
                            }
                            else {
                                return (
                                    <td key={key}>
                                        {data[key] ?
                                                data[key] 
                                            : 
                                                '-'
                                        }
                                    </td>
                                );
                            }
                        })}
                </tr>
            ));

        buttonRender = (page['buttons'] || []).map((button, index) => {
                const entries = Object.entries(button);
            
                // First entry is always the button label and its action
                const [label, action] = entries[0];
            
                // Optionally look for 'Filter'
                const filterEntry = entries.find(([key]) => key === 'Filter');
                const filterKeyword = filterEntry ? filterEntry[1] : undefined;

                return (
                    <Button
                        key={index}
                        onClick={() => actionFunction(label, action, filterKeyword)}
                        buttonText={label}
                        selected={label === selectedButton}
                    />
                );
            });
    }

    return (
        <>
            <Helmet>
                <meta name='robots' content='noindex' />
            </Helmet>
            <div className={styles.placeholder} />
            {loading ? (
                <Loader />
            ) : (
                <div className={styles.container}>
                    <h1>{page.name}</h1>
                    <p>{totalNumberOfItems} results</p>

                    {page.name === "Invoices" ? 
                        <p>{formatCurrency(total)}</p>
                    :
                        <p></p>
                    }

                    <div className={styles.inputContainer}>
                        <InputField
                            path='search.png'
                            placeholder='Search...'
                            value={kwargs}
                            onChange={(e) => setKwargs(kwargsFixer(e.target.value))}
                        />
                    </div>

                    {userRole === 'Admin' ? (
                        <div className={styles.buttonContainer}>{buttonRender}</div>
                    ) : null}

                    <div className={styles.tableWrapper}>
                        <table>
                            <thead>
                                <tr>
                                    <th></th>
                                    {page.tableHeader.map((header) => (<th key={header}>{header}</th>))}
                                </tr>
                            </thead>
                            <tbody>
                                {fetchLoading && window.scrollY < 400 ? (
                                    <tr className={styles.loaderWrappingCell}>
                                        <td colSpan={page.tableHeader.length + 1}>
                                            <div className={styles.tableLoader} />
                                        </td>
                                    </tr>
                                ) : (
                                    dataRender
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div ref={loadMoreRef} style={{ width: '100%', height: '50px', marginTop: '70px', textAlign: 'center'}}>
                        {fetchLoading ? <p>Loading..</p> : <p>You've reached the end!</p>}
                    </div>
                </div>
            )}
        </>
    );
};

export default Viewer;

export const kwargsFixer = (kwargs) => {
    return kwargs.replace(/[^a-zA-Z0-9- ]/g, '').toLowerCase();
};
